import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Building2, Plus, Search, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Org {
  id: string;
  name: string;
  slug: string;
  business_email: string | null;
  business_phone: string | null;
  industry: string | null;
  created_at: string;
}

export default function ClientsAdmin() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleCheckDone, setRoleCheckDone] = useState(false);

  // Give useAuth time to fetch the role (it sets loading=false before isAdmin resolves)
  useEffect(() => {
    if (authLoading) return;
    const t = setTimeout(() => setRoleCheckDone(true), 800);
    return () => clearTimeout(t);
  }, [authLoading]);

  useEffect(() => {
    if (authLoading) return;
    if (isAdmin) {
      loadOrgs();
    }
  }, [authLoading, isAdmin]);

  const loadOrgs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("organizations" as any)
      .select("id, name, slug, business_email, business_phone, industry, created_at")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Failed to load clients", description: error.message, variant: "destructive" });
    } else {
      setOrgs((data as any) || []);
    }
    setLoading(false);
  };

  const filtered = orgs.filter((o) =>
    [o.name, o.slug, o.business_email, o.industry]
      .filter(Boolean)
      .some((v) => v!.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <Helmet>
        <title>Clients | iQ Texting Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
              </Button>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Clients
              </h1>
            </div>
            <Button onClick={() => navigate("/onboarding")}>
              <Plus className="h-4 w-4 mr-2" /> New Client
            </Button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6">
          <Card className="p-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients by name, email, industry..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search clients"
              />
            </div>
          </Card>

          {authLoading || (!roleCheckDone && !isAdmin) ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : !isAdmin ? (
            <Card className="p-10 text-center">
              <h2 className="text-lg font-semibold mb-1">Admin access required</h2>
              <p className="text-sm text-muted-foreground mb-4">You don't have permission to view clients.</p>
              <Button onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
            </Card>
          ) : loading ? (
            <p className="text-muted-foreground">Loading clients...</p>
          ) : filtered.length === 0 ? (
            <Card className="p-10 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <h2 className="text-lg font-semibold mb-1">No clients yet</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Get started by onboarding your first client organization.
              </p>
              <Button onClick={() => navigate("/onboarding")}>
                <Plus className="h-4 w-4 mr-2" /> Add Your First Client
              </Button>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filtered.map((org) => (
                <Card key={org.id} className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{org.name}</h3>
                      <Badge variant="outline">{org.slug}</Badge>
                      {org.industry && <Badge variant="secondary">{org.industry}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {org.business_email || "No email"} · {org.business_phone || "No phone"}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/dashboard?org=${org.id}`)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" /> Open
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
}
